import { Box, Card, CardContent, CardHeader, SxProps, Theme, Typography, useMediaQuery } from '@mui/material'
import dayjs from 'dayjs'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { FC, useEffect, useRef, useState } from 'react'
import {
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalDto,
  AnimalHealthDtoPathologies,
  AnimalHealthDtoSterilizedForFerrets,
  AnimalHealthDtoSterilizedOrNeutered,
  AnimalHealthOperationDto,
  AnimalHealthOperationDtoType,
  AnimalHealthTestResultDtoResult,
  AnimalHealthTestResultDtoType,
} from '../../../../interactors/gen/backendClient'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { useFetchOrRefreshMembersOnMount } from '../../../../store/useFetchOrRefreshMembersOnMount'
import { isInEnum } from '../../../../utils/isInEnum'
import { omitEmptyString } from '../../../../utils/omitEmptyString'
import { OfferUsageLimitBanner } from '../../../common/OfferUsageLimitBanner'
import { getAnimalHealthEntriesQuota } from '../../../../domain/Customer/ContentLimits'
import { useLimitedContentAccess } from '../../../../utils/hooks/useLimitedContentAccess'
import { spacingItem, theme } from '../../../theme'
import { surfaceCardSx } from '../../../common/surfaceCardSx'
import { AnimalHealthNewOperationModal, AnimalHealthOperations } from './AnimalHealthOperations'
import { AnimalHealthVaccines } from './AnimalHealthVaccines'

export const getMostRecentVaccinationOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const vaccinationTypes: AnimalHealthOperationDtoType[] = [
    AnimalHealthOperationDtoType.FirstInjection,
    AnimalHealthOperationDtoType.SecondInjection,
    AnimalHealthOperationDtoType.ThirdInjection,
    AnimalHealthOperationDtoType.AnnualReminder,
  ]

  return operations.reduce((mostRecent: AnimalHealthOperationDto | null, operation: AnimalHealthOperationDto) => {
    const isVaccinationType = operation.type.some((type) => vaccinationTypes.includes(type))

    if (!isVaccinationType) {
      return mostRecent
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')
    const mostRecentDate = mostRecent ? dayjs(mostRecent.date, 'DD/MM/YYYY') : dayjs(0)

    return operationDate.isAfter(mostRecentDate) ? operation : mostRecent
  }, null)
}

export const getSecondMostRecentVaccinationOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const vaccinationTypes: AnimalHealthOperationDtoType[] = [
    AnimalHealthOperationDtoType.FirstInjection,
    AnimalHealthOperationDtoType.SecondInjection,
    AnimalHealthOperationDtoType.ThirdInjection,
    AnimalHealthOperationDtoType.AnnualReminder,
  ]

  let mostRecent: AnimalHealthOperationDto | null = null
  let secondMostRecent: AnimalHealthOperationDto | null = null

  operations.forEach((operation) => {
    const isVaccinationType = operation.type.some((type) => vaccinationTypes.includes(type))

    if (!isVaccinationType) {
      return
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')

    if (!mostRecent || operationDate.isAfter(dayjs(mostRecent.date, 'DD/MM/YYYY'))) {
      secondMostRecent = mostRecent
      mostRecent = operation
    } else if (
      (!secondMostRecent || operationDate.isAfter(dayjs(secondMostRecent.date, 'DD/MM/YYYY'))) &&
      operationDate.isBefore(dayjs(mostRecent.date, 'DD/MM/YYYY'))
    ) {
      secondMostRecent = operation
    }
  })

  return secondMostRecent
}

export const getMostRecentDewormingOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const dewormingType: AnimalHealthOperationDtoType = AnimalHealthOperationDtoType.Deworming

  return operations.reduce((mostRecent: AnimalHealthOperationDto | null, operation: AnimalHealthOperationDto) => {
    const isDewormingType = operation.type.some((type) => type === dewormingType)

    if (!isDewormingType) {
      return mostRecent
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')
    const mostRecentDate = mostRecent ? dayjs(mostRecent.date, 'DD/MM/YYYY') : dayjs(0)

    return operationDate.isAfter(mostRecentDate) ? operation : mostRecent
  }, null)
}

export const getSecondMostRecentDewormingOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const dewormingType = 'deworming'

  let mostRecent: AnimalHealthOperationDto | null = null
  let secondMostRecent: AnimalHealthOperationDto | null = null

  operations.forEach((operation) => {
    const isDewormingType = operation.type.some((type) => type === dewormingType)

    if (!isDewormingType) {
      return
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')

    if (!mostRecent || operationDate.isAfter(dayjs(mostRecent.date, 'DD/MM/YYYY'))) {
      secondMostRecent = mostRecent
      mostRecent = operation
    } else if (
      (!secondMostRecent || operationDate.isAfter(dayjs(secondMostRecent.date, 'DD/MM/YYYY'))) &&
      operationDate.isBefore(dayjs(mostRecent.date, 'DD/MM/YYYY'))
    ) {
      secondMostRecent = operation
    }
  })

  return secondMostRecent
}

export const getMostRecentFleaControlOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const fleaControlType: AnimalHealthOperationDtoType = AnimalHealthOperationDtoType.FleaControl

  return operations.reduce((mostRecent: AnimalHealthOperationDto | null, operation: AnimalHealthOperationDto) => {
    const isFleaControlType = operation.type.some((type) => type === fleaControlType)

    if (!isFleaControlType) {
      return mostRecent
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')
    const mostRecentDate = mostRecent ? dayjs(mostRecent.date, 'DD/MM/YYYY') : dayjs(0)

    return operationDate.isAfter(mostRecentDate) ? operation : mostRecent
  }, null)
}

export const getSecondMostRecentFleaControlOperation = (
  operations: AnimalHealthOperationDto[]
): AnimalHealthOperationDto | null => {
  const fleaControlType = 'flea-control'

  let mostRecent: AnimalHealthOperationDto | null = null
  let secondMostRecent: AnimalHealthOperationDto | null = null

  operations.forEach((operation) => {
    const isFleaControlType = operation.type.some((type) => type === fleaControlType)

    if (!isFleaControlType) {
      return
    }

    const operationDate = dayjs(operation.date, 'DD/MM/YYYY')

    if (!mostRecent || operationDate.isAfter(dayjs(mostRecent.date, 'DD/MM/YYYY'))) {
      secondMostRecent = mostRecent
      mostRecent = operation
    } else if (
      (!secondMostRecent || operationDate.isAfter(dayjs(secondMostRecent.date, 'DD/MM/YYYY'))) &&
      operationDate.isBefore(dayjs(mostRecent.date, 'DD/MM/YYYY'))
    ) {
      secondMostRecent = operation
    }
  })

  return secondMostRecent
}

const getOperationDate = (operation: AnimalHealthOperationDto) => {
  if (!operation.date) {
    return null
  }
  const parsedDate = dayjs(operation.date, 'DD/MM/YYYY')
  return parsedDate.isValid() ? parsedDate : null
}

const isSterilizationEffective = (operation: AnimalHealthOperationDto) => {
  const operationDate = getOperationDate(operation)
  return !operationDate || operationDate.isBefore(dayjs(), 'day') || operationDate.isSame(dayjs(), 'day')
}

interface Props {
  animal: AnimalDto
  sx?: SxProps<Theme>
}

export const AnimalHealthOperationsCard: FC<Props> = ({ animal, sx }) => {
  useFetchOrRefreshMembersOnMount()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [operations, setOperations] = useState<AnimalHealthOperationDto[]>(animal.health.operations)
  const limitedContentAccess = useLimitedContentAccess()
  const healthOperationsQuota = getAnimalHealthEntriesQuota(limitedContentAccess, operations)
  const hasReachedHealthOperationsLimit = healthOperationsQuota != null && healthOperationsQuota.remaining === 0
  const parsedOperationPrices = operations
    .map((operation) => operation.price?.trim())
    .filter((price): price is string => Boolean(price))
    .map((price) => Number(price.replace('€', '').replace(',', '.').trim()))
    .filter((price) => !Number.isNaN(price))
  const missingOperationPricesCount = operations.filter((operation) => !operation.price?.trim()).length
  const totalOperationsCost = parsedOperationPrices.reduce((sum, price) => sum + price, 0)
  const formattedOperationsCost = `${totalOperationsCost.toLocaleString('fr-FR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(totalOperationsCost) ? 0 : 2,
  })} €`

  const initialOperationsRef = useRef(animal.health.operations)

  const editAnimalMutation = useEditAnimalMutation({})

  const canEditAnimal = useCanEditAnimal()

  const hasScheduledSterilizationOperation = operations.some(
    (operation) =>
      operation.type.includes(AnimalHealthOperationDtoType.SterilizedOrNeutered) && !isSterilizationEffective(operation)
  )

  useEffect(() => {
    if (isEqual(operations, animal.health.operations)) return
    setOperations(animal.health.operations)
    console.log('rerender')
  }, [animal.health.operations])

  useEffect(() => {
    const saveOperations = async () => {
      const operationsHaveChanged = JSON.stringify(operations) !== JSON.stringify(initialOperationsRef.current)
      if (!operationsHaveChanged && !canEditAnimal) {
        return
      }

      interface ResultsType {
        [type: string]: AnimalHealthOperationDto
      }

      interface TestResult {
        date: string
        result: AnimalHealthTestResultDtoResult
      }

      interface MostRecentPositiveTestResults {
        [key: string]: TestResult
      }

      const mostRecentPositiveTestResults: MostRecentPositiveTestResults = {}

      operations.forEach((operation) => {
        const currentDate = dayjs(operation.date, 'DD/MM/YYYY')
        operation.testResults?.forEach((testResult) => {
          const { type, result } = testResult
          // Check if the result is positive and either the type hasn't been added or the current date is more recent
          if (
            result === 'positive' &&
            (!mostRecentPositiveTestResults[type] ||
              currentDate.isAfter(dayjs(mostRecentPositiveTestResults[type].date, 'DD/MM/YYYY')))
          ) {
            mostRecentPositiveTestResults[type] = {
              date: operation.date,
              result: result,
            }
          }
        })
      })

      // Update the pathologies based on the most recent positive test results
      const updatedPathologiesWithTest: AnimalHealthTestResultDtoType[] = Object.keys(
        mostRecentPositiveTestResults
      ).reduce((acc: AnimalHealthTestResultDtoType[], type) => {
        acc.push(type as AnimalHealthTestResultDtoType) // Cast type to your specific enum/type if necessary
        return acc
      }, [])

      const oldPathologiesWithoutTest = animal.health.pathologies?.filter(
        (pathology) => !isInEnum(AnimalHealthTestResultDtoType, pathology)
      )

      const updatedPathologies = [...(oldPathologiesWithoutTest ?? []), ...updatedPathologiesWithTest]

      const sterilizationOperations = operations.filter((operation) =>
        operation.type.includes(AnimalHealthOperationDtoType.SterilizedOrNeutered)
      )
      const hasEffectiveSterilization = sterilizationOperations.some(isSterilizationEffective)
      const hasScheduledSterilization = sterilizationOperations.some(
        (operation) => !isSterilizationEffective(operation)
      )

      const noSterilizationOperations = sterilizationOperations.length === 0
      const sterilizationStatusFromOperations = hasEffectiveSterilization
        ? ('true' as AnimalHealthDtoSterilizedOrNeutered)
        : hasScheduledSterilization
        ? ('false' as AnimalHealthDtoSterilizedOrNeutered)
        : noSterilizationOperations && animal.health.sterilizedOrNeutered === 'true'
        ? ('unknown' as AnimalHealthDtoSterilizedOrNeutered)
        : animal.health.sterilizedOrNeutered

      const shouldSyncSterilizationStatus = sterilizationStatusFromOperations !== animal.health.sterilizedOrNeutered

      if (!operationsHaveChanged && !shouldSyncSterilizationStatus) {
        return
      }

      const resultsByType = operations.reduce((results: ResultsType, operation) => {
        const currentDate = dayjs(operation.date, 'DD/MM/YYYY')
        operation.type.forEach((type) => {
          if (
            (operation.identificationNumber || operation.tattooNumber) &&
            (type === AnimalHealthOperationDtoType.Tatoo || type === AnimalHealthOperationDtoType.IdentificationChip)
          ) {
            if (!results[type] || currentDate.isAfter(dayjs(results[type].date, 'DD/MM/YYYY'))) {
              results[type] = operation
            }
          }
        })

        if (
          operation.type.includes(AnimalHealthOperationDtoType.SterilizedOrNeutered) &&
          isSterilizationEffective(operation)
        ) {
          const existing = results.sterilizedOrNeutered
          if (!existing) {
            results.sterilizedOrNeutered = operation
          } else {
            const existingDate = getOperationDate(existing)
            const currentOperationDate = getOperationDate(operation)
            if (!existingDate && currentOperationDate) {
              results.sterilizedOrNeutered = operation
            } else if (existingDate && currentOperationDate && currentOperationDate.isAfter(existingDate)) {
              results.sterilizedOrNeutered = operation
            }
          }
        }

        if (operation.type.includes(AnimalHealthOperationDtoType.Implant)) {
          if (!results.implant || currentDate.isAfter(dayjs(results.implant.date, 'DD/MM/YYYY'))) {
            results.implant = operation
          }
        }

        return results
      }, {} as ResultsType)

      console.log('resultsByType', resultsByType.implant)

      const mostRecentVaccinationOperation = getMostRecentVaccinationOperation(operations)
      if (
        animal.vaccination.vaccinationStatus === 'completed' &&
        mostRecentVaccinationOperation &&
        animal.vaccination.annualReminderFrequencyInYear !== -1
      ) {
        if (dayjs(mostRecentVaccinationOperation.date, 'DD/MM/YYYY').isBefore(dayjs(), 'days')) {
          if (!mostRecentVaccinationOperation.needsValidation || mostRecentVaccinationOperation.validated) {
            const formattedDate = dayjs(mostRecentVaccinationOperation.date, 'DD/MM/YYYY')
              .add(animal.vaccination.annualReminderFrequencyInYear || 1, 'year')
              .format('DD/MM/YYYY')
            operations.push({
              id: nanoid(),
              date: formattedDate,
              type: [AnimalHealthOperationDtoType.AnnualReminder],
              needsValidation: true,
              additionalInfo:
                'Rendez-vous créé automatiquement à la prochaine date de rappel annuel conseillée. Vous pouvez modifier cette date si besoin.',
            })
          }
        }
      }

      const mostRecentDewormingOperation = getMostRecentDewormingOperation(operations)
      if (mostRecentDewormingOperation && animal.health.dewormingFrequencyInMonths !== -1) {
        const delayToAdd =
          animal.health.dewormingFrequencyInMonths === 0.5 ? 15 : animal.health.dewormingFrequencyInMonths || 1
        const unityToAdd = animal.health.dewormingFrequencyInMonths === 0.5 ? 'days' : 'months'
        if (dayjs(mostRecentDewormingOperation.date, 'DD/MM/YYYY').isBefore(dayjs(), 'days')) {
          if (!mostRecentDewormingOperation.needsValidation || mostRecentDewormingOperation.validated) {
            const formattedDate = dayjs(mostRecentDewormingOperation.date, 'DD/MM/YYYY')
              .add(delayToAdd, unityToAdd)
              .format('DD/MM/YYYY')
            operations.push({
              id: nanoid(),
              date: formattedDate,
              type: [AnimalHealthOperationDtoType.Deworming],
              needsValidation: true,
              additionalInfo:
                'Rendez-vous créé automatiquement à la prochaine date de rappel de vermifuge conseillée. Vous pouvez modifier cette date si besoin.',
            })
          }
        }
      }

      const mostRecentFleaControlOperation = getMostRecentFleaControlOperation(operations)
      if (mostRecentFleaControlOperation && animal.health.fleaControlFrequencyInMonths !== -1) {
        const delayToAdd =
          animal.health.fleaControlFrequencyInMonths === 0.5 ? 15 : animal.health.fleaControlFrequencyInMonths || 1
        const unityToAdd = animal.health.fleaControlFrequencyInMonths === 0.5 ? 'days' : 'months'
        if (dayjs(mostRecentFleaControlOperation.date, 'DD/MM/YYYY').isBefore(dayjs(), 'days')) {
          if (!mostRecentFleaControlOperation.needsValidation || mostRecentFleaControlOperation.validated) {
            const formattedDate = dayjs(mostRecentFleaControlOperation.date, 'DD/MM/YYYY')
              .add(delayToAdd, unityToAdd)
              .format('DD/MM/YYYY')
            operations.push({
              id: nanoid(),
              date: formattedDate,
              type: [AnimalHealthOperationDtoType.FleaControl],
              needsValidation: true,
              additionalInfo:
                "Rendez-vous créé automatiquement à la prochaine date de rappel d'antipuce conseillée. Vous pouvez modifier cette date si besoin.",
            })
          }
        }
      }
      const euthanasia = operations.find((operation) =>
        operation.type.includes(AnimalHealthOperationDtoType.Euthanasia)
      )
      const euthanasiaDate =
        euthanasia && euthanasia.date !== undefined && euthanasia.date !== ''
          ? dayjs(euthanasia.date, 'DD/MM/YYYY')
          : null

      const { documents, ...animalWithoutDocuments } = animal
      const update = omitEmptyString({
        ...animalWithoutDocuments,
        adoption: {
          ...animal.adoption,
          goodbyeDate:
            euthanasiaDate && (!animal.adoption.goodbyeDate || animal.adoption.goodbyeDate === '')
              ? euthanasiaDate.toISOString()
              : animal.adoption.goodbyeDate,
          status: euthanasiaDate ? AnimalAdoptionDtoStatus.CannotBeAdopted : animal.adoption.status,
          cannotBeAdoptedStatus: euthanasiaDate
            ? AnimalAdoptionDtoCannotBeAdoptedStatus.Dead
            : animal.adoption.cannotBeAdoptedStatus,
          goodbyeDetails:
            euthanasiaDate && (!animal.adoption.goodbyeDetails || animal.adoption.goodbyeDetails === '')
              ? 'Euthanasie'
              : animal.adoption.goodbyeDetails,
        },
        tattoo: {
          ...animal.tattoo,
          number: resultsByType[AnimalHealthOperationDtoType.Tatoo]
            ? resultsByType[AnimalHealthOperationDtoType.Tatoo]?.tattooNumber
            : animal.tattoo?.number,
        },
        identificationNumber: resultsByType[AnimalHealthOperationDtoType.IdentificationChip]
          ? (resultsByType[AnimalHealthOperationDtoType.IdentificationChip]?.identificationNumber as string)
          : animal.identificationNumber,
        health: {
          ...animal.health,
          sterilizedOrNeutered: sterilizationStatusFromOperations,
          sterilizedForFerrets:
            sterilizationStatusFromOperations === 'true'
              ? AnimalHealthDtoSterilizedForFerrets.Sterilized
              : resultsByType.implant
              ? AnimalHealthDtoSterilizedForFerrets.Implanted
              : undefined,
          operations: operations,
          pathologies: updatedPathologies as AnimalHealthDtoPathologies[],
        },
      })

      setOperations(operations)
      await editAnimalMutation.mutateAsync(update)
    }

    saveOperations()
  }, [operations, canEditAnimal])

  return (
    <>
      <Card sx={sx ? ([surfaceCardSx, sx] as SxProps<Theme>) : surfaceCardSx}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0.75, sm: 2 },
                width: '100%',
              }}
            >
              <Typography sx={{ fontSize: { xs: 15.5, sm: 16 }, fontWeight: 700, lineHeight: 1.2 }}>
                {isMobile ? '💉 RDV de santé' : '💉 Historique et rendez-vous de santé:'}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 12.5, sm: 13 },
                  fontWeight: 700,
                  color: '#2B4166',
                  lineHeight: 1.25,
                  textAlign: { xs: 'left', sm: 'right' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {`Coût de santé : ${formattedOperationsCost}`}
                {missingOperationPricesCount > 0 && ` (${missingOperationPricesCount} manquant)`}
              </Typography>
            </Box>
          }
        />
        <CardContent sx={{ backgroundColor: 'rgba(246, 248, 252, 0.72)' }}>
          {healthOperationsQuota != null && (
            <OfferUsageLimitBanner
              title={
                healthOperationsQuota.used > healthOperationsQuota.max
                  ? `${healthOperationsQuota.used} actes de santé enregistrés`
                  : `${healthOperationsQuota.used} acte${healthOperationsQuota.used > 1 ? 's' : ''} de santé sur ${
                      healthOperationsQuota.max
                    } utilisé${healthOperationsQuota.used > 1 ? 's' : ''}`
              }
              description={
                healthOperationsQuota.used > healthOperationsQuota.max
                  ? `La fiche de ${animal.name} dépasse actuellement la limite de ${healthOperationsQuota.max} actes incluse dans l'offre gratuite. Les données existantes restent accessibles, mais vous devez passer à l'offre Essentielle ou configurer Stripe Connect pour enregistrer un nouvel acte.`
                  : hasReachedHealthOperationsLimit
                  ? `La fiche de ${animal.name} a atteint la limite incluse dans l'offre gratuite. Passez à l'offre Essentielle ou configurez Stripe Connect pour enregistrer de nouveaux actes et bénéficier d'un historique de santé illimité.`
                  : `Il reste ${healthOperationsQuota.remaining} acte${
                      healthOperationsQuota.remaining > 1 ? 's' : ''
                    } de santé à enregistrer sur la fiche de ${
                      animal.name
                    } avec l'offre gratuite. Passez à l'offre Essentielle ou configurez Stripe Connect pour un historique de santé illimité.`
              }
              currentCount={healthOperationsQuota.used}
              limit={healthOperationsQuota.max}
              ctaLabel="Découvrir l'offre Essentielle"
            />
          )}
          <Box sx={{ width: '100%', marginBottom: 3 }}>
            <AnimalHealthVaccines animal={animal} canEditAnimal={canEditAnimal} />
          </Box>
          {hasScheduledSterilizationOperation && (
            <Box sx={spacingItem}>
              <Typography variant="body2" color="text.secondary">
                Une date de stérilisation future n&#39;active pas le statut &#34;stérilisé&#34;. Il sera mis à jour à la
                date prévue.
              </Typography>
            </Box>
          )}
          <AnimalHealthOperations operations={operations} setOperations={setOperations} animal={animal} />
          <Box
            display="flex"
            justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
            sx={{
              position: 'fixed',
              left: { xs: 16, sm: 'auto' },
              right: { xs: 16, sm: 24 },
              bottom: { xs: 16, sm: 24 },
              zIndex: 1200,
              width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
            }}
          >
            <AnimalHealthNewOperationModal
              animal={animal}
              operations={operations}
              setOperations={setOperations}
              disabled={!useCanEditAnimal() || hasReachedHealthOperationsLimit}
              disabledReason={`Vous avez atteint la limite de ${healthOperationsQuota?.max} actes de santé incluse dans l'offre gratuite.`}
            />
          </Box>
        </CardContent>
      </Card>
    </>
  )
}
